import React, { useState, useEffect } from 'react';
import { useApplicationStore } from '../../stores/applicationStore';
import { useAuthStore } from '../../stores/authStore';
import { Interview } from '../../types';
import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { X } from 'lucide-react';

interface EditInterviewModalProps {
  interview: Interview & { companyName: string; position: string; applicationId: string };
  isOpen: boolean;
  onClose: () => void;
  onInterviewUpdated: () => void;
}

export const EditInterviewModal: React.FC<EditInterviewModalProps> = ({
  interview,
  isOpen,
  onClose,
  onInterviewUpdated
}) => {
  const { applications, updateApplicationInFirestore } = useApplicationStore();
  const { user } = useAuthStore();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: '',
    time: '',
    type: interview.type,
    location: interview.location,
    notes: interview.notes,
    reminder: interview.reminder,
    contact: interview.contact || '',
    contactPerson: interview.contactPerson || '',
    contactEmail: interview.contactEmail || '',
    contactPhone: interview.contactPhone || '',
    contactRole: interview.contactRole || '',
  });

  // Inizializza il form quando cambia l'interview
  useEffect(() => {
    if (interview) {
      const interviewDate = new Date(interview.date.seconds * 1000);
      setForm({
        date: format(interviewDate, 'yyyy-MM-dd'),
        time: format(interviewDate, 'HH:mm'),
        type: interview.type,
        location: interview.location,
        notes: interview.notes,
        reminder: interview.reminder,
        contact: interview.contact || '',
        contactPerson: interview.contactPerson || '',
        contactEmail: interview.contactEmail || '',
        contactPhone: interview.contactPhone || '',
        contactRole: interview.contactRole || '',
      });
    }
  }, [interview]);

  const handleSave = async () => {
    if (!user?.uid || !form.date || !form.time) return;
    
    setSaving(true);
    try {
      const application = applications.find(app => app.id === interview.applicationId);
      if (!application) return;

      const interviewDateTime = new Date(`${form.date}T${form.time}`);
      const updatedInterview: Interview = {
        ...interview,
        date: Timestamp.fromDate(interviewDateTime),
        type: form.type as 'Presenza' | 'Online' | 'Telefono',
        location: form.location,
        notes: form.notes,
        reminder: form.reminder,
        contact: form.contact || undefined,
        contactPerson: form.contactPerson || undefined,
        contactEmail: form.contactEmail || undefined,
        contactPhone: form.contactPhone || undefined,
        contactRole: form.contactRole || undefined,
      };

      // Aggiorna l'interview nell'array degli interviews
      const updatedInterviews = application.interviews.map(intv => 
        intv.id === interview.id ? updatedInterview : intv
      );

      await updateApplicationInFirestore(user.uid, application.id, {
        interviews: updatedInterviews,
      });

      onInterviewUpdated();
      onClose();
    } catch (error) {
      console.error('Errore nell\'aggiornamento colloquio:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.uid || !window.confirm('Sei sicuro di voler eliminare questo colloquio?')) return;
    
    setSaving(true);
    try {
      const application = applications.find(app => app.id === interview.applicationId);
      if (!application) return;

      // Rimuovi l'interview dall'array
      const updatedInterviews = application.interviews.filter(intv => intv.id !== interview.id);

      await updateApplicationInFirestore(user.uid, application.id, {
        interviews: updatedInterviews,
      });

      onInterviewUpdated();
      onClose();
    } catch (error) {
      console.error('Errore nell\'eliminazione colloquio:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-800 w-full h-full sm:rounded-2xl sm:shadow-2xl sm:max-w-md sm:max-w-lg md:max-w-xl sm:h-auto flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Modifica Colloquio
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {interview.companyName} - {interview.position}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Data e Ora */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  📅 Data
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  🕐 Ora
                </label>
                <input
                  type="time"
                  value={form.time}
                  onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                />
              </div>
            </div>
            
            {/* Tipo */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                📞 Tipo Colloquio
              </label>
                              <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value as 'Presenza' | 'Online' | 'Telefono' }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                >
                  <option value="Presenza">🏢 Presenza</option>
                  <option value="Online">💻 Online</option>
                  <option value="Telefono">📱 Telefono</option>
                </select>
            </div>
            
            {/* Luogo/Link */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                📍 Luogo/Link
              </label>
              <input
                type="text"
                value={form.location}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                placeholder="Indirizzo, link meeting, ecc."
              />
            </div>

            {/* Contatto */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                👤 Contatto
              </label>
              <input
                type="text"
                value={form.contact}
                onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                placeholder="Nome del referente, email, telefono..."
              />
            </div>

            {/* Informazioni di Contatto Dettagliate */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600 pb-2">
                📋 Informazioni di Contatto
              </h4>
              
              {/* Persona di Riferimento */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  👤 Persona di Riferimento
                </label>
                <input
                  type="text"
                  value={form.contactPerson}
                  onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                  placeholder="Nome e cognome..."
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  📧 Email
                </label>
                <input
                  type="email"
                  value={form.contactEmail}
                  onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                  placeholder="email@azienda.com"
                />
              </div>

              {/* Telefono */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  📱 Numero di Telefono
                </label>
                <input
                  type="tel"
                  value={form.contactPhone}
                  onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                  placeholder="+41 79 000 12 12"
                />
              </div>

              {/* Ruolo */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🏢 Ruolo
                </label>
                <input
                  type="text"
                  value={form.contactRole}
                  onChange={e => setForm(f => ({ ...f, contactRole: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                  placeholder="HR Manager, Recruiter, ecc."
                />
              </div>
            </div>
            
            {/* Note */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                📝 Note
              </label>
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all resize-none"
                placeholder="Note aggiuntive..."
              />
            </div>
            
            {/* Checkbox Reminder */}
            <div className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <input
                type="checkbox"
                id="reminder"
                checked={form.reminder}
                onChange={e => setForm(f => ({ ...f, reminder: e.target.checked }))}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all"
              />
              <label htmlFor="reminder" className="ml-3 block text-sm font-medium text-gray-900 dark:text-white">
                🔔 Attiva reminder per questo colloquio
              </label>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 p-4 pb-16 sm:pb-28 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex gap-3">
            <button
              onClick={handleDelete}
              disabled={saving}
              className="flex-1 px-3 py-2.5 border-2 border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all font-medium text-sm"
            >
              🗑️ Elimina
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium text-sm"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !form.date || !form.time}
              className="flex-1 px-3 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-lg"
            >
              {saving ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Salvando...
                </span>
              ) : (
                '💾 Salva'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 